import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { SupportedFlagTypes } from '@atlassiansox/feature-flag-web-client';
import { action, observable, runInAction } from 'mobx';

import FeatureFlagsClient, { FeatureFlagValues } from '../clients/FeatureFlagsClient';
import { captureFatal } from '../sentry';
import { ErrorSource } from '../sentry/types';

import { ApiStore } from './base/apiStore';

export const defaultFeatureFlags: FeatureFlagValues = {
  'uchi.enable-nav-v3': false,
  'redirect-start-traffic-to-atlas_q1io5': false,
  post_office_in_uchi_switcher: false,
};

export default class FeatureFlagsStore extends ApiStore {
  @observable flags: FeatureFlagValues = defaultFeatureFlags;

  @action
  async fetchFlags(featureFlagsClient?: FeatureFlagsClient) {
    try {
      this.setLoadingApiState();
      if (!featureFlagsClient) {
        throw new Error('featureFlagClient is null or undefined');
      }

      await featureFlagsClient.initialiseClient();

      try {
        /**
         * Platform Feature flags (PFF): https://developer.atlassian.com/cloud/framework/atlassian-frontend/development/feature-flagging/platform-feature-flags/
         *
         * This creates a PFF resolver that allows calls to `getBooleanFF` inside of Platform components to use your feature flag client
         * and LD project, rather than installing a feature flag client of their own, or passing those flags in via props.
         */
        setBooleanFeatureFlagResolver(flagKey => Boolean(featureFlagsClient.getFlagValue(flagKey, false)));
      } catch (error) {
        captureFatal(error, { source: ErrorSource.FEATURE_FLAGS_STORE });
      }

      const flags: Record<string, SupportedFlagTypes> = {};
      for (const [key, flag] of Object.entries(featureFlagsClient.getFlags())) {
        flags[key] = flag.value;
      }

      runInAction(() => {
        this.flags = {
          ...this.flags,
          ...flags,
        };
      });
      this.setSuccessApiState();
    } catch (e) {
      captureFatal(e, { source: ErrorSource.FEATURE_FLAGS_STORE });
      this.setErrorApiState(e);
    }
  }
}
